import {
  applyToPoint,
  atLength,
  invert,
  maxScale,
  nodeCommands,
  rulerOf,
  subpathsOfCommands,
  widthAt,
  type NodeRecord,
  type Ruler,
  type WidthPoint,
} from '@flobi/artwork-core';
import { BRUSHES, brushChosen, convertedToPath } from './liquify';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * The Width tool: a stroke that is not the same thickness all the way along.
 *
 * Drag away from a path and the stroke swells where you dragged; drag back towards it and
 * it narrows. What a drag leaves behind is a WIDTH POINT — a place on the path with a
 * thickness — and the stroke walks between one and the next, so three or four of them
 * describe a whole tapering line. Illustrator has had this since CS5 and it is the one
 * way to draw a line that thickens like a brushstroke without the line stopping being a
 * line: the path is still a path, still editable, still one anchor list.
 *
 * Its marks are diamonds rather than squares, because they are not anchors. An anchor
 * says where the line goes; a width point says how fat it is there, and two things that
 * are edited differently should not look the same.
 */

/** How near a width point or a path counts as being, in screen pixels. */
const REACH = 8;
/** Hand-drag of nothing: below this a press is a press rather than a drag. */
const SLOP = 2;
/** The fattest a stroke may get, as a multiple of its weight. */
const WIDEST = 20;
/** How near an edge has to come to another one before it sticks to it, in screen pixels. */
const STICK = 6;
/**
 * The most an edge may stick across, as a share of the stroke's weight.
 *
 * Six pixels is a hair on a heavy stroke and the whole stroke on a thin one: at weight 1
 * the plain weight's pull covered every width from nothing to six and a half, so a thin
 * line jumped from plain to fat and could never be tapered (QA 2026-09-25,
 * arc-width-warp-F5). Held to a quarter of the weight, a thin stroke can be drawn down to
 * a point and a heavy one still sticks as it did.
 */
const STICK_SHARE = 0.25;
/**
 * How far round a closed outline the first drag tapers back to the plain weight, either
 * side, as a share of the way round. A closed path has no ends to taper to, so without
 * these the one width point made the whole outline fat (arc-width-warp-F9).
 */
const CLOSED_TAPER = 0.25;
/** The most width points the document keeps on one stroke (the engine's `widths` schema). */
const MOST_MARKS = 64;

interface Held {
  /** Not read-only: a line is turned into a path on the first drag, and gets a new id. */
  id: string;
  readonly at: number;
  readonly from: Point;
  /** Which side of the line the press started on, so a drag widens the side it grabbed. */
  readonly side: 1 | -1;
  /** Both sides as they were, so a drag reads as one movement rather than many. */
  readonly was: { left: number; right: number };
  /** Option held: one side moves and the other stays where it was. */
  readonly oneSided: boolean;
  moved: boolean;
  /** The stroke already has MOST_MARKS and this drag has been told so. */
  full?: boolean;
}

/** A path node's first subpath as a ruler, with what is needed to read a press against it. */
interface Measured {
  readonly ruler: Ruler;
  readonly closed: boolean;
  readonly widths: WidthPoint[];
  readonly weight: number;
  readonly scale: number;
  toLocal(world: Point): Point;
}

/** Layers with a stroke that this tool cannot take hold of, so a press on one says so. */
const SHAPES_NOT_PATHS = new Set(['rect', 'ellipse', 'star', 'polygon', 'text', 'image']);

export function widthTool(): Tool {
  let held: Held | null = null;
  /** The path under the pointer, so its width points are shown before anything is pressed. */
  let over: string | null = null;
  /**
   * Which width point is chosen, said as WHERE it is rather than as its place in the list.
   *
   * The list is kept in order along the path, so writing one re-sorts it and an index
   * means a different mark a moment later — which had a drag deleting the far end of the
   * stroke on its second frame instead of updating the point under the hand. Where it
   * sits does not move while it is being widened, so where it sits is its name.
   */
  let chosen: number | null = null;
  /** The width the drag agreed with, so the overlay can say why it stopped there. */
  let agreed: { at: number; share: number } | null = null;
  const sameMark = (a: number, b: number) => Math.abs(a - b) < 1e-6;

  /**
   * The line a node's stroke runs along: a path's own first subpath, or the line a Line
   * layer draws.
   *
   * Lines too, because the Line tool makes a `line` layer rather than a path, and this
   * tool used to ignore every one of them — no cursor, nothing on a drag, and the press
   * cleared the selection (QA 2026-09-25, arc-width-warp-N4). Width points live only on
   * paths, so a line is turned into one on its first drag (see `onPointerMove`), the
   * way liquify turns a shape into one on its first touch.
   */
  const lineOf = (node: NodeRecord) => {
    if (node.type === 'path') return node.path?.subpaths?.[0];
    if (node.type === 'line') return subpathsOfCommands([...(nodeCommands(node) ?? [])])[0];
    return undefined;
  };

  const measure = (ctx: ToolContext, id: string): Measured | null => {
    const node = ctx.node(id);
    if (!node || ctx.scene.lockedHere(id)) return null;
    const subpath = lineOf(node);
    if (!subpath || subpath.points.length < 2) return null;
    const world = ctx.scene.worldTransform(id);
    const back = invert(world);
    if (!back) return null;
    return {
      ruler: rulerOf(subpath.points, subpath.closed, 16),
      closed: subpath.closed,
      widths: node.type === 'path' ? [...((node.widths ?? []) as WidthPoint[])] : [],
      weight: (node as { strokeStyle?: { weight?: number } }).strokeStyle?.weight ?? 1,
      scale: maxScale(world) || 1,
      toLocal: (at) => applyToPoint(back, at.x, at.y),
    };
  };

  /** How far along a path a point is, from 0 to 1, and how far off it that leaves it. */
  const nearestOn = (measured: Measured, local: Point): { t: number; away: number; side: 1 | -1 } | null => {
    const { ruler } = measured;
    if (ruler.points.length < 2 || ruler.length <= 0) return null;
    let best = { t: 0, away: Infinity, side: 1 as 1 | -1 };
    for (let index = 0; index < ruler.points.length - 1; index++) {
      const a = ruler.points[index]!;
      const b = ruler.points[index + 1]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const span = dx * dx + dy * dy;
      const share = span <= 1e-12 ? 0 : Math.max(0, Math.min(1, ((local.x - a.x) * dx + (local.y - a.y) * dy) / span));
      const on = { x: a.x + dx * share, y: a.y + dy * share };
      const away = Math.hypot(local.x - on.x, local.y - on.y);
      if (away >= best.away) continue;
      const distance = ruler.at[index]! + (ruler.at[index + 1]! - ruler.at[index]!) * share;
      // Which side: the sign of the cross product of the heading and the reach.
      const cross = dx * (local.y - on.y) - dy * (local.x - on.x);
      best = { t: ruler.length <= 0 ? 0 : distance / ruler.length, away, side: cross >= 0 ? 1 : -1 };
    }
    return best.away === Infinity ? null : best;
  };

  /**
   * Where a width point sits on the page, and how far its two sides reach.
   *
   * Both edges are worked out in the path's own coordinates, where the stroke is drawn,
   * and then put through its matrix. The heading is measured there too, so turning it
   * into a page direction without the matrix left the diamonds off the stroke's edges on
   * a rotated or flipped line, and a press on the edge you could see made a new width
   * point instead of taking this one (QA 2026-09-25, arc-width-warp-F17).
   */
  const markOf = (ctx: ToolContext, id: string, measured: Measured, mark: WidthPoint) => {
    const world = ctx.scene.worldTransform(id);
    const along = atLength(measured.ruler, mark.at * measured.ruler.length);
    if (!along) return null;
    const radians = (along.angle * Math.PI) / 180;
    const normal = { x: -Math.sin(radians), y: Math.cos(radians) };
    const edge = (share: number) =>
      applyToPoint(world, along.x + normal.x * measured.weight * share, along.y + normal.y * measured.weight * share);
    return {
      on: applyToPoint(world, along.x, along.y),
      left: edge(mark.left),
      right: edge(-mark.right),
    };
  };

  /**
   * The paths and lines near a box, however deep they sit.
   *
   * `nearby` answers in render units, and a path inside a group is drawn as part of its
   * group — so it came back as the group, which is not a path, and a stroke inside any
   * group could not be reached at all (QA 2026-09-25, arc-width-warp-N3). Each unit is
   * opened into the visible, unlocked paths and lines inside it that come near the box.
   */
  const candidatesNear = (ctx: ToolContext, room: { x: number; y: number; width: number; height: number }): string[] => {
    const out: string[] = [];
    for (const unit of ctx.nearby(room)) {
      for (const id of ctx.scene.subtree(unit)) {
        const type = ctx.node(id)?.type;
        if (type !== 'path' && type !== 'line') continue;
        if (ctx.scene.hiddenHere(id) || ctx.scene.lockedHere(id)) continue;
        const box = ctx.boundsOf(id);
        if (!box) continue;
        const meets =
          box.x <= room.x + room.width && box.x + box.width >= room.x && box.y <= room.y + room.height && box.y + box.height >= room.y;
        if (meets) out.push(id);
      }
    }
    return out;
  };

  /** The path under the pointer: the one whose line the pointer is nearest to. */
  const under = (ctx: ToolContext, world: Point): string | null => {
    const reach = REACH * ctx.unitsPerPixel;
    const box = { x: world.x - reach, y: world.y - reach, width: reach * 2, height: reach * 2 };
    // Wider than the pointer, because the width points sit out at the edge of the stroke
    // and a press on one of those is a press on this path.
    const room = { x: box.x - reach * 8, y: box.y - reach * 8, width: box.width + reach * 16, height: box.height + reach * 16 };
    let best: { id: string; away: number } | null = null;
    for (const id of candidatesNear(ctx, room)) {
      const measured = measure(ctx, id);
      if (!measured) continue;
      const found = nearestOn(measured, measured.toLocal(world));
      if (!found) continue;
      // As far out as the stroke actually reaches THERE, not as far as its weight: a
      // width point that has been pulled out to five times the weight sits five times
      // further from the line, and a press on it has to count as a press on this path or
      // there is no way to take hold of the thing you just made.
      const share = widthAt(measured.widths, found.t, measured.closed);
      const away = found.away * measured.scale;
      const allow = reach + Math.max(share.left, share.right) * measured.weight * measured.scale;
      if (away <= allow && (!best || away < best.away)) best = { id, away };
    }
    return best?.id ?? null;
  };

  /**
   * The width a drag settles on: what the hand asked for, or a width already on the path.
   *
   * A silhouette reads as one shape only when the places that should match actually
   * match, and by hand they never quite do — a run of width points a hair apart leaves a
   * step at every one of them. So a dragged edge sticks to the widths already there: the
   * other side of the same point first, because a stroke that is the same either side is
   * the commonest thing to want, then every other width point, then the stroke's own
   * weight, which is the way back to a plain line.
   *
   * Measured in document units rather than in shares, so how near "near" is does not
   * change with the weight — but never more than a quarter of the weight (STICK_SHARE),
   * so a thin stroke is not all pull. Nothing at all is a target too, so a drag can take
   * a stroke down to a point.
   */
  const sticking = (
    measured: Measured,
    held: Held,
    side: 1 | -1,
    loose: number,
    reach: number,
  ): { share: number; agreed: { at: number; share: number } | null } => {
    const weight = measured.weight || 1;
    const mine = measured.widths.find((mark) => sameMark(mark.at, held.at));
    const others = measured.widths.filter((mark) => !sameMark(mark.at, held.at));
    const candidates: { at: number; share: number }[] = [];
    // The other side of this very point, when the drag is only moving one of them.
    if (held.oneSided && mine) candidates.push({ at: held.at, share: side === 1 ? mine.right : mine.left });
    for (const mark of others) candidates.push({ at: mark.at, share: mark.left }, { at: mark.at, share: mark.right });
    candidates.push({ at: held.at, share: 0.5 }, { at: held.at, share: 0 });

    let best: { at: number; share: number } | null = null;
    let closest = reach;
    for (const candidate of candidates) {
      const away = Math.abs(candidate.share - loose) * weight * measured.scale;
      if (away > closest) continue;
      closest = away;
      best = candidate;
    }
    return best ? { share: best.share, agreed: best } : { share: loose, agreed: null };
  };

  const write = (ctx: ToolContext, id: string, widths: WidthPoint[]): void => {
    const tidy = [...widths].sort((a, b) => a.at - b.at).map((mark) => ({ at: mark.at, left: mark.left, right: mark.right }));
    ctx.edit([{ op: 'set', id, props: { widths: tidy } }]);
  };

  return {
    id: 'width',

    deactivate(ctx) {
      held = null;
      over = null;
      chosen = null;
      agreed = null;
      ctx.setCursor(null);
    },

    onPointerDown(event, world, ctx) {
      const id = under(ctx, world);
      if (!id) {
        chosen = null;
        // Empty canvas lets go of the selection. A layer this tool cannot use does not:
        // the press used to clear the selection there too, with nothing to say why
        // (arc-width-warp-N3, N4). A shape that has a stroke but is not a path is told so.
        const hit = ctx.hit(world);
        if (!hit) {
          ctx.setSelection([]);
          return true;
        }
        const type = ctx.node(hit)?.type;
        if (type && SHAPES_NOT_PATHS.has(type)) ctx.notice('The Width tool works on paths and lines.');
        return true;
      }
      const measured = measure(ctx, id);
      if (!measured) return true;
      ctx.setSelection([id]);
      const local = measured.toLocal(world);
      const found = nearestOn(measured, local);
      if (!found) return true;

      // A press on a mark already there takes hold of it; anywhere else on the line makes
      // a new one where the press landed.
      // The NEAREST mark within reach: the last one used to win, so of two close together
      // a press on one took its neighbour (QA 2026-09-25, arc-width-warp-F27).
      let closest = REACH * ctx.unitsPerPixel;
      let grabbed: WidthPoint | null = null;
      for (const mark of measured.widths) {
        const drawn = markOf(ctx, id, measured, mark);
        if (!drawn) continue;
        const near = Math.min(
          Math.hypot(drawn.left.x - world.x, drawn.left.y - world.y),
          Math.hypot(drawn.right.x - world.x, drawn.right.y - world.y),
        );
        if (near > closest) continue;
        closest = near;
        grabbed = mark;
      }
      const was = grabbed ?? widthAt(measured.widths, found.t, measured.closed);
      chosen = grabbed ? grabbed.at : found.t;
      held = {
        id,
        at: grabbed ? grabbed.at : found.t,
        from: world,
        side: found.side,
        was: { left: was.left, right: was.right },
        oneSided: event.altKey,
        moved: false,
      };
      ctx.refresh();
      return true;
    },

    onPointerMove(event, world, ctx) {
      if (!held) {
        const found = under(ctx, world);
        if (found !== over) {
          over = found;
          ctx.refresh();
        }
        ctx.setCursor(found ? 'ew-resize' : null);
        return;
      }
      if (event.buttons === 0) return;
      let measured = measure(ctx, held.id);
      if (!measured) return;
      const away = Math.hypot(world.x - held.from.x, world.y - held.from.y);
      if (!held.moved && away < SLOP * ctx.unitsPerPixel) return;
      held.moved = true;

      // A Line layer has nowhere to keep width points, so the first real drag turns it
      // into the path it draws — same look, same place in the stack, and anything that
      // followed it following the path (arc-width-warp-N4). Not on the press: a click
      // that never drags leaves the line a line.
      const node = ctx.node(held.id);
      if (node?.type === 'line') {
        const line = lineOf(node);
        const made = line ? convertedToPath(ctx, held.id, [line]) : null;
        if (!made) {
          held = null;
          return;
        }
        held.id = made;
        ctx.setSelection([made]);
        measured = measure(ctx, made);
        if (!measured) return;
      }

      // How far the hand is from the line now, in weights: that IS the new half-width,
      // so the stroke's edge follows the pointer instead of lagging behind a factor.
      const found = nearestOn(measured, measured.toLocal(world));
      if (!found) return;
      const loose = Math.max(0, Math.min(WIDEST, found.away / (measured.weight || 1)));
      // ⌘ held: exactly where the hand is, sticking to nothing — the same key that turns
      // snapping off for the Transform and Artboard tools (arc-width-warp-F5).
      const stick = event.metaKey ? 0 : Math.min(STICK * ctx.unitsPerPixel, (measured.weight || 1) * measured.scale * STICK_SHARE);
      const stuck = stick > 0 ? sticking(measured, held, found.side, loose, stick) : { share: loose, agreed: null };
      agreed = stuck.agreed;
      const share = stuck.share;
      // Which side of the line the hand is on decides which side grows. Without option
      // held both grow together, which is what a person means by "make this thicker".
      const left = held.oneSided && found.side === -1 ? held.was.left : share;
      const right = held.oneSided && found.side === 1 ? held.was.right : share;

      const widths = measured.widths.filter((mark) => !sameMark(mark.at, held!.at));
      widths.push({ at: held.at, left, right });
      // A path with one width point is a path of one thickness, which is not what the
      // drag said. So the first drag on a plain stroke leaves the two ends at the weight
      // they already had, giving the swell somewhere to taper back to.
      const first = measured.widths.length === 0;
      if (first && !measured.closed) {
        if (!sameMark(held.at, 0)) widths.push({ at: 0, left: 0.5, right: 0.5 });
        if (!sameMark(held.at, 1)) widths.push({ at: 1, left: 0.5, right: 0.5 });
      }
      // A closed one has no ends, so it tapers back a quarter of the way round either
      // side, wrapping over the seam; the profile goes round (widthAt's `closed`), so the
      // rest of the outline stays plain (QA 2026-09-25, arc-width-warp-F9).
      if (first && measured.closed) {
        const wrap = (at: number) => ((at % 1) + 1) % 1;
        widths.push({ at: wrap(held.at - CLOSED_TAPER), left: 0.5, right: 0.5 }, { at: wrap(held.at + CLOSED_TAPER), left: 0.5, right: 0.5 });
      }
      // The document keeps at most MOST_MARKS on a stroke and refuses the whole edit past
      // that, so a drag somewhere new on a full stroke did nothing and said nothing
      // (QA 2026-09-25, arc-width-warp-F26). Said once a drag.
      if (widths.length > MOST_MARKS) {
        if (!held.full) ctx.notice(`A stroke holds up to ${MOST_MARKS} width points.`);
        held.full = true;
        return;
      }
      write(ctx, held.id, widths);
      chosen = held.at;
      ctx.refresh();
    },

    onPointerUp(_event, _world, ctx) {
      held = null;
      agreed = null;
      ctx.stopCapturing();
      ctx.refresh();
    },

    onKeyDown(event, ctx) {
      if (event.key !== 'Backspace' && event.key !== 'Delete') return false;
      const id = ctx.selection[0];
      const measured = id ? measure(ctx, id) : null;
      const gone = chosen;
      if (!id || !measured || gone === null || !measured.widths.some((mark) => sameMark(mark.at, gone))) return false;
      event.preventDefault();
      write(
        ctx,
        id,
        measured.widths.filter((mark) => !sameMark(mark.at, gone)),
      );
      chosen = null;
      ctx.stopCapturing();
      ctx.refresh();
      return true;
    },

    /**
     * The same eight marks as liquify, because Illustrator keeps them in one flyout and
     * they are one family: the tools that change a stroke without moving the line.
     */
    rail(ctx) {
      const items: RailButton[] = [
        {
          id: 'width',
          icon: 'width' as const,
          title: 'Width — Drag away from a path to thicken its stroke, or inward to thin it.',
          panelGroup: 'Stroke',
          pressed: true,
          run: () => {},
        },
        ...BRUSHES.map((brush) => ({
          id: brush.id,
          icon: brush.icon,
          title: brush.title,
          panelGroup: brush.panelGroup,
          pressed: false,
          run: () => {
            brushChosen(brush.id);
            ctx.chooseTool('liquify');
          },
        })),
      ];
      const id = ctx.selection[0];
      const node = id ? ctx.node(id) : undefined;
      const weight = node?.type === 'path' ? (node.strokeStyle?.weight ?? 1) : ctx.style.strokeWeight;
      return {
        top: { kind: 'buttons' as const, items },
        bottom: {
          kind: 'slider' as const,
          icon: 'weight' as const,
          title: 'Stroke weight',
          value: Math.round(weight),
          min: 1,
          max: 60,
          onChange: (value: number) => {
            if (node?.type === 'path') ctx.edit([{ op: 'set', id, props: { strokeStyle: { ...node.strokeStyle, weight: value } } }]);
            else ctx.drawWith({ strokeWeight: value });
            ctx.railChanged();
          },
        },
      };
    },

    /**
     * The line and its width points, drawn as diamonds out at the edge of the stroke.
     *
     * Out at the edge rather than on the line, because that is the thing being dragged —
     * a mark in the middle of a stroke would say "move me along" when what it does is
     * "pull me out". The line itself is traced too: a stroke that varies hides where its
     * path actually runs, and this tool is about that path.
     */
    draw(overlay, ctx) {
      const id = held?.id ?? ctx.selection[0] ?? over;
      const measured = id ? measure(ctx, id) : null;
      if (!id || !measured) return;
      const world = ctx.scene.worldTransform(id);
      overlay.curve(
        measured.ruler.points.map((at) => ({ at: applyToPoint(world, at.x, at.y) })),
        { width: 1 },
      );
      const size = 5;
      measured.widths.forEach((mark) => {
        const drawn = markOf(ctx, id, measured, mark);
        if (!drawn) return;
        for (const corner of [drawn.left, drawn.right]) {
          const reach = size * ctx.unitsPerPixel;
          overlay.curve(
            [
              { at: { x: corner.x, y: corner.y - reach } },
              { at: { x: corner.x + reach, y: corner.y } },
              { at: { x: corner.x, y: corner.y + reach } },
              { at: { x: corner.x - reach, y: corner.y } },
              { at: { x: corner.x, y: corner.y - reach } },
            ],
            { width: chosen !== null && sameMark(mark.at, chosen) ? 2 : 1 },
          );
        }
        overlay.line(drawn.left, drawn.right, { width: 1, alpha: 0.5 });
      });

      // Why the drag stopped where it did: a line from the edge under the hand to the
      // edge it agreed with. Drawn in the guide's own colour, because it is the same
      // thing a moved shape draws when it lines up with another one.
      if (!agreed || !held) return;
      const from = measured.widths.find((mark) => sameMark(mark.at, held!.at));
      const to = measured.widths.find((mark) => sameMark(mark.at, agreed!.at));
      const here = from && markOf(ctx, id, measured, from);
      const there = to && markOf(ctx, id, measured, to);
      if (!here || !there) return;
      const pick = (mark: { left: Point; right: Point }, on: WidthPoint) =>
        Math.abs(on.left - agreed!.share) <= Math.abs(on.right - agreed!.share) ? mark.left : mark.right;
      overlay.guide(pick(here, from!), pick(there, to!));
    },
  };
}
